<template>
  <div class="step-result">
    <!-- 当選セクション -->
    <div class="result-section">
      <!-- 当選メッセージ -->
      <div class="form-group">
        <label class="form-label">
          {{ '当選メッセージ' }}
          <span class="required-badge">{{ '必須' }}</span>
        </label>
        <el-input
          v-model="result.winMessage"
          type="textarea"
          :rows="3"
          class="win-message-field"
          :class="{ 'is-error': errors[4] && errors[4].winMessage }"
          :placeholder="'おめでとうございます！当選です！'"
        />
        <div v-if="errors[4] && errors[4].winMessage" class="input-invalid-text">
          {{ errors[4].winMessage }}
        </div>
      </div>

      <!-- 当選メディア -->
      <div class="form-group">
        <label class="form-label">{{ '当選メディア' }}</label>
        <div class="result-media-section" v-loading="store.uploadLoading">
          <!-- プレビュー表示 -->
          <div v-if="result.winUrl" class="media-preview win-media-preview">
            <i class="mdi mdi-close" @click.stop="handleWinMediaDelete"></i>
            <div class="media-name">{{ getWinMediaFileName() }}</div>
            <!-- 画像プレビュー -->
            <img v-if="isWinImageFile()" :src="result.winUrl" alt="当選メディアプレビュー" class="direct-media" />
            <!-- 動画プレビュー -->
            <video v-else-if="!isWinImageFile()" :src="result.winUrl" controls class="direct-media"></video>
          </div>

          <!-- ファイル選択ボタン（プレビューがない場合のみ表示） -->
          <div v-else class="media-upload-container">
            <el-button class="upload-btn win-media-upload-btn" @click="handleWinMediaSelectClick">
              <i class="mdi mdi-image-plus" />
              {{ '画像・動画を追加' }}
            </el-button>
            <p class="upload-hint">
              {{ 'アップロード可能な最大サイズ: 画像8MB 動画25MB' }}
            </p>
            <!-- 非表示のファイル入力 -->
            <input
              id="winMediaFileInput"
              type="file"
              accept="image/png,image/jpeg,video/mp4"
              style="display: none"
              @change="handleWinMediaFileChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 落選セクション -->
    <div class="result-section">
      <!-- 落選メッセージ -->
      <div class="form-group">
        <label class="form-label">
          {{ '落選メッセージ' }}
          <span class="required-badge">{{ '必須' }}</span>
        </label>
        <el-input
          v-model="result.loseMessage"
          type="textarea"
          :rows="3"
          class="lose-message-field"
          :class="{ 'is-error': errors[4] && errors[4].loseMessage }"
          :placeholder="'残念ながら今回は落選です。次回もご参加ください！'"
        />
        <div v-if="errors[4] && errors[4].loseMessage" class="input-invalid-text">
          {{ errors[4].loseMessage }}
        </div>
      </div>

      <!-- 落選メディア -->
      <div class="form-group">
        <label class="form-label">{{ '落選メディア' }}</label>
        <div class="result-media-section" v-loading="store.uploadLoading">
          <!-- プレビュー表示 -->
          <div v-if="result.loseUrl" class="media-preview lose-media-preview">
            <i class="mdi mdi-close" @click.stop="handleLoseMediaDelete"></i>
            <div class="media-name">{{ getLoseMediaFileName() }}</div>
            <!-- 画像プレビュー -->
            <img v-if="isLoseImageFile()" :src="result.loseUrl" alt="落選メディアプレビュー" class="direct-media" />
            <!-- 動画プレビュー -->
            <video v-else-if="!isLoseImageFile()" :src="result.loseUrl" controls class="direct-media"></video>
          </div>

          <!-- ファイル選択ボタン（プレビューがない場合のみ表示） -->
          <div v-else class="media-upload-container">
            <el-button class="upload-btn lose-media-upload-btn" @click="handleLoseMediaSelectClick">
              <i class="mdi mdi-image-plus" />
              {{ '画像・動画を追加' }}
            </el-button>
            <p class="upload-hint">
              {{ 'アップロード可能な最大サイズ: 画像8MB 動画25MB' }}
            </p>
            <!-- 非表示のファイル入力 -->
            <input
              id="loseMediaFileInput"
              type="file"
              accept="image/png,image/jpeg,video/mp4"
              style="display: none"
              @change="handleLoseMediaFileChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { storeToRefs } from 'pinia'
import { getCurrentInstance, watch } from 'vue'

import { useInstagramScenarioEditorStore } from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioEditorStore'
import { TrackingService } from '@/client/services'

const { proxy } = getCurrentInstance() as any

// Store
const store = useInstagramScenarioEditorStore()
const { result, errors } = storeToRefs(store)

const getFileExtension = (name: string): string => {
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index).toLowerCase() : ''
}

const isSupportedImageExtension = (name: string): boolean => {
  return ['.jpg', '.jpeg', '.png'].includes(getFileExtension(name))
}

/**
 * 当選メディアが画像かどうかを判定
 */
const isWinImageFile = (): boolean => {
  if (!result.value.winUrl) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (result.value.winFileType) {
    return result.value.winFileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = result.value.winUrl.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || result.value.winUrl.startsWith('data:image/')
}

/**
 * 落選メディアが画像かどうかを判定
 */
const isLoseImageFile = (): boolean => {
  if (!result.value.loseUrl) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (result.value.loseFileType) {
    return result.value.loseFileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = result.value.loseUrl.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || result.value.loseUrl.startsWith('data:image/')
}

/**
 * 当選メディアファイル名を取得
 */
const getWinMediaFileName = (): string => {
  // fileNameフィールドがあればそれを優先
  if (result.value.winFileName) {
    return result.value.winFileName
  }

  // blob URLの場合は拡張子から生成
  if (result.value.winUrl && result.value.winUrl.startsWith('blob:')) {
    const fileType = result.value.winFileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (result.value.winUrl) {
    const urlWithoutQuery = result.value.winUrl.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return '当選メディア'
}

/**
 * 落選メディアファイル名を取得
 */
const getLoseMediaFileName = (): string => {
  // fileNameフィールドがあればそれを優先
  if (result.value.loseFileName) {
    return result.value.loseFileName
  }

  // blob URLの場合は拡張子から生成
  if (result.value.loseUrl && result.value.loseUrl.startsWith('blob:')) {
    const fileType = result.value.loseFileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (result.value.loseUrl) {
    const urlWithoutQuery = result.value.loseUrl.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return '落選メディア'
}

/**
 * 当選メディア選択ボタンのクリックハンドラ
 */
const handleWinMediaSelectClick = () => {
  const fileInput = document.getElementById('winMediaFileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

/**
 * 落選メディア選択ボタンのクリックハンドラ
 */
const handleLoseMediaSelectClick = () => {
  const fileInput = document.getElementById('loseMediaFileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

/**
 * メディアファイル変更の共通処理
 */
const handleMediaFileChange = async (event: Event, mediaType: 'win' | 'lose') => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック
  const imageTypes = ['image/png', 'image/jpeg']
  const videoTypes = ['video/mp4']

  const isImage = imageTypes.includes(file.type)
  const isVideo = videoTypes.includes(file.type)

  if (!isImage && !isVideo) {
    proxy.$notify({
      title: '対応形式はpng、jpg、mp4です。',
      customClass: 'danger',
      duration: 5000
    })
    // ファイル入力をリセット
    target.value = ''
    return
  }

  if (isImage && !isSupportedImageExtension(file.name)) {
    proxy.$notify({
      title: '対応形式はpng、jpg、mp4です。',
      customClass: 'danger',
      duration: 5000
    })
    target.value = ''
    return
  }

  // ファイルサイズチェック（画像8MB未満、動画25MB未満）
  const maxSize = isImage ? 8 * 1024 * 1024 : 25 * 1024 * 1024
  const sizeLabel = isImage ? '8MB' : '25MB'
  const fileTypeLabel = isImage ? '画像' : '動画'

  if (file.size >= maxSize) {
    proxy.$notify({
      title: `${fileTypeLabel}ファイルのサイズは${sizeLabel}未満にしてください。`,
      customClass: 'danger',
      duration: 5000
    })
    // ファイル入力をリセット
    target.value = ''
    return
  }

  // 画像/動画ともにAPIアップロードを実行し、mediaId を保存する
  try {
    const uploadResult = await store.uploadMedia(file)
    const fileName = uploadResult.mediaOriginName || file.name
    const updates =
      mediaType === 'win'
        ? {
            winMediaId: uploadResult.mediaId,
            winUrl: uploadResult.url,
            winFileType: (isImage ? 'image' : 'video') as const,
            winFileName: fileName
          }
        : {
            loseMediaId: uploadResult.mediaId,
            loseUrl: uploadResult.url,
            loseFileType: (isImage ? 'image' : 'video') as const,
            loseFileName: fileName
          }
    store.updateResult(updates)
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_result_media',
        screen: 'InstagramScenarioEditorDialog.StepResult',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: `result_${mediaType}`,
        fileType: isImage ? 'image' : 'video',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    proxy.$notify({
      title: 'メディアのアップロードに失敗しました',
      customClass: 'danger',
      duration: 5000
    })
  }

  // ファイル入力をリセット
  target.value = ''
}

/**
 * 当選メディアファイル変更ハンドラ
 */
const handleWinMediaFileChange = async (event: Event) => {
  await handleMediaFileChange(event, 'win')
}

/**
 * 落選メディアファイル変更ハンドラ
 */
const handleLoseMediaFileChange = async (event: Event) => {
  await handleMediaFileChange(event, 'lose')
}

/**
 * 当選メディア削除ハンドラ
 */
const handleWinMediaDelete = () => {
  // Blob URLの場合はメモリ解放
  if (result.value.winUrl && result.value.winUrl.startsWith('blob:')) {
    URL.revokeObjectURL(result.value.winUrl)
  }
  store.clearWinMedia()
}

/**
 * 落選メディア削除ハンドラ
 */
const handleLoseMediaDelete = () => {
  // Blob URLの場合はメモリ解放
  if (result.value.loseUrl && result.value.loseUrl.startsWith('blob:')) {
    URL.revokeObjectURL(result.value.loseUrl)
  }
  store.clearLoseMedia()
}

/**
 * 当落メッセージの変更を監視してリアルタイムバリデーション
 */
watch(
  result,
  () => {
    store.validateStep4()
  },
  { deep: true }
)
</script>

<style scoped lang="scss">
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.step-result {
  .result-section {
    padding: $spacing-unit * 2;
    margin-bottom: $spacing-unit * 3;
    border: 1px solid $border-color;
    border-radius: $round;

    &:last-child {
      margin-bottom: 0;
    }

    .section-title {
      margin: 0 0 $spacing-unit * 2 0;
      font-size: 16px;
      font-weight: bold;
      color: $text-color;
    }

    .form-group {
      margin-bottom: $spacing-unit * 2;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .form-label {
      display: block;
      margin-bottom: $spacing-unit;
      font-size: 14px;
      font-weight: 500;
      color: $text-color;

      .required-badge {
        display: inline-block;
        padding: 2px 8px;
        margin-left: $spacing-unit;
        font-size: 12px;
        font-weight: normal;
        color: $text-color-white;
        background-color: $danger-color;
        border-radius: 12px;
      }
    }

    .input-invalid-text {
      margin-top: math.div($spacing-unit, 2);
      font-size: 14px;
      color: $danger-color;
    }

    .is-error {
      :deep(.el-textarea__inner) {
        border-color: $danger-color !important;
      }
    }

    // メディアアップロードセクション
    .result-media-section {
      position: relative;
      min-height: 120px;

      .media-upload-container {
        display: flex;
        flex-direction: row;
        align-items: center;

        .upload-btn {
          margin-right: $spacing-unit * 2;

          i {
            margin-right: math.div($spacing-unit, 2);
          }
        }

        .upload-hint {
          margin: 0;
          font-size: 12px;
          color: $sub-text-color;
        }
      }

      .media-preview {
        position: relative;
        display: inline-block;
        padding: math.div($spacing-unit, 2) $spacing-unit;
        border: 1px solid $border-color;
        border-radius: $round;

        .media-name {
          padding-right: $spacing-unit * 3;
          margin-bottom: $spacing-unit;
          font-size: 14px;
          font-weight: 500;
          color: $text-color;
          word-break: break-all;
        }

        .mdi-close {
          position: absolute;
          top: math.div($spacing-unit, 2);
          right: math.div($spacing-unit, 2);
          font-size: 18px;
          color: $text-color;
          cursor: pointer;

          &:hover {
            color: $danger-color;
          }
        }

        .direct-media {
          display: block;
          max-width: 100%;
          max-height: $spacing-unit * 32;
        }
      }
    }
  }
}
</style>
